import React, { useEffect, useState } from 'react';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@material-ui/core';
import { Container } from "react-bootstrap";
import Button from "@material-ui/core/Button";
import { deleteOnboardingDocument, getKycDetails, uploadKycDetails } from '../../store/actions/userOnboarding';
import { convertFilesToBase64 } from '../../config/utils';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CancelIcon from '@material-ui/icons/Cancel';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import './onboarding.css';
import { allowedDocExtensions } from '../../constant';
export default function KycDetailsUpload(props) {
    const {isViewUserRequest, activeStep, handleBack, handleNext, isApprovedSeller} = props;
    const [kycList, setKycList] = React.useState([]);
    const [loader, setLoader] = React.useState(false);
    const [maxSize] = useState(5);
    
    const validateAttachmentFiles = (files) => {
        if (files.length === 0) return { isValid: false, validFiles: [] };

        if (files.length > 5) {
            toast.info(`Only ${5} files can be uploaded at one time`, 1000);
            return { isValid: false, validFiles: [] };
        }

        let validFiles = [];
        let totalSizeBytes = 0;

        files.forEach((file) => {
            const extension = file.name.split(".").pop()?.toLowerCase();
            const fileSizeBytes = file.size;

            if (!allowedDocExtensions.includes(extension)) {
                toast.error(`Unsupported file format: ${file.name}`, 1000);
                return;
            }
            totalSizeBytes += fileSizeBytes;
            file.isNew = true;
            validFiles.push(file);
        });

        if (totalSizeBytes > maxSize * 1024 * 1024) {
            toast.info(`Total file size should not exceed ${maxSize}MB`, 1000);
            return { isValid: false, validFiles: [] };
        }

        return { isValid: validFiles.length > 0, validFiles };
    };

    const handleFileChange = (e, item) => {
        const files = [...e.target.files];
        if (!files || files.length === 0) return;
        let {validFiles, isValid} = validateAttachmentFiles(files);
        if (!isValid) {
            return;
        }
        let listData = [...kycList];
        const index = listData.findIndex(i => i.DocumentMasterId === item.DocumentMasterId);
        if (index === -1) return;

        convertFilesToBase64(validFiles).then((base64Array) => {
                listData[index].file = base64Array; // Assuming single file upload, take the first file
                setKycList(listData);
        }).catch((error) => {
            console.error(error);
        });
    }

    useEffect(() => {
        // Fetch KYC details when the component mounts
        fetchDocList();
    }, []);

    const fetchDocList = async() => {
        let data = {
            "onboardingId": 0,
            "documentTypeId": 1,
            "countryId": 0
        }
        if (isViewUserRequest) {
            data.onboardingId = props.data.Id;
        }
        setLoader(true);
        let res = await getKycDetails(data, isApprovedSeller);
        if(res?.data?.Status === 200) {
            if (res?.data?.EntityList?.length) {
                let list = [...res.data.EntityList];
                let result = list
                    .map(item => item.DocumentMasterId)
                    .filter((id, index, arr) => arr.indexOf(id) === index)
                    .map(id => {
                        let records = list.filter(x => x.DocumentMasterId === id);

                        let files = records
                        .filter(x => x.DocumentFileUrl)
                        .map(x => ({
                            DocumentFileUrl: x.DocumentFileUrl,
                            OnboardingDocumentId: x.OnboardingDocumentId,
                            DocumentFileName: x.DocumentFileName
                        }));

                        return {
                        ...records[0],
                        DocumentFileUrl: files
                        };
                    });
                console.log("KYC List:", result);
                setKycList(result || []);
            }
        }
        setLoader(false);
    }

    const handleDelete = (item, fileIndex) => {
        let listData = [...kycList];
        let index = listData.findIndex(i => i.DocumentMasterId === item.DocumentMasterId);
        if(index !== -1) {
            listData[index].file.splice(fileIndex, 1);
            setKycList(listData);
        }
    };

    const deleteDocument = async (item, doc) => {
        if (isApprovedSeller) {
            return;
        }
        let data = {
            onboardingDocumentId: doc.OnboardingDocumentId,
        }
        let res = await deleteOnboardingDocument(data, isApprovedSeller);
        if (res?.data?.Status === 200) {
            toast.success("Document deleted successfully.");
            let listData = [...kycList];
            let index = listData.findIndex(i => i.DocumentMasterId === item.DocumentMasterId);
            if (index !== -1) {
                listData[index].DocumentFileUrl = listData[index].DocumentFileUrl.filter(url => url.OnboardingDocumentId !== doc.OnboardingDocumentId);
                console.log("Updated KYC List after deletion:", listData);
                setKycList(listData);
            }
        } else {
            toast.error("Failed to delete document. Please try again.");
        }
    };

    const handleSave = async () => {
        let data = {
            attachmentsList: kycList
                .filter(item => item.file)
                .flatMap(item =>
                    item.file.map(f => ({
                        onboardingDocumentId: 0,
                        onboardingId: isViewUserRequest
                            ? props.data.Id
                            : 0,
                        documentMasterId: item.DocumentMasterId,
                        uploadedFile: f.file,
                        documentFileUrl: "",
                        documentFileName: f.name,
                        eSigned: false,
                        uploadedSignaturePath: ""
                    }))
                )
        };

        if (data.attachmentsList.length === 0) {
            handleNext();
            return;
        }
        setLoader(true);
        let res = await uploadKycDetails(data, isApprovedSeller);
        setLoader(false);   
        if (res?.data?.Status === 200) {
            toast.success("KYC details uploaded successfully.");
            handleNext();
        } else {
            toast.error("Failed to upload KYC details. Please try again.");
        }
    }

    return (
        <Container fluid>
            <Paper className='onboardingFormWrapping fw-3' elevation={2} square>
                <div className="container-fluid">
                    <Typography variant="h6">KYC Details</Typography>
                    {loader && (
                        <div className="full-screen-spinner-overlay">
                            <CircularProgress disableShrink />
                        </div>
                    )}
                    <div className="table-responsive">
                        <table className="table-onboarding align-middle">
                            <thead>
                                <tr>
                                    <th>KYC Category</th>
                                    <th>Document Type</th>
                                    <th className="text-center">Upload</th>
                                </tr>
                            </thead>
                            <tbody>
                                {kycList.map((item, index) => (
                                    <tr key={item.DocumentMasterId}>
                                        <td>{item.DocumentName}</td>
                                        <td className="text-muted">{item.DocumentDescription}</td>
                                        <td className="text-center">
                                            <IconButton color="primary" aria-label="Upload KYC Document" component="label">
                                                <input
                                                    type="file"
                                                    hidden
                                                    data-testid={`kyc-file-input-${item.DocumentMasterId}`}
                                                    multiple
                                                    onChange={(e) => handleFileChange(e, item)}
                                                />
                                                <Tooltip arrow title="Upload">
                                                    <CloudUploadIcon style={{color: item?.DocumentFileUrl.length > 0 ? '#4caf50' : undefined}}/>
                                                </Tooltip>
                                            </IconButton>
                                            <div className=''>
                                                {item?.DocumentFileUrl.map((doc) => (
                                                    <Chip
                                                        key={`${item.DocumentMasterId}`}
                                                        variant="outlined"
                                                        size="small"
                                                        onDelete={isApprovedSeller ? undefined : () => deleteDocument(item, doc)}
                                                        deleteIcon={isApprovedSeller ? undefined : <Tooltip arrow title="Delete"><CancelIcon style={{ color: "red" }} /></Tooltip>}
                                                        icon={<AttachFileIcon aria-label={`delete-${doc.DocumentFileName}`} />}
                                                        label={<a href={doc.DocumentFileUrl} target="_blank" rel="noopener noreferrer" className="onboarding-ellipsis" title={doc.DocumentFileName}>{doc.DocumentFileName}</a>}
                                                        className="onboarding-chip"
                                                    />
                                                ))}
                                            </div>
                                            <div>
                                                {item?.file?.map((file, fileIndex) => (
                                                    <Chip
                                                        key={item.DocumentMasterId}
                                                        variant="outlined"
                                                        size="small"
                                                        onDelete={() => handleDelete(item, fileIndex)}
                                                        icon={<AttachFileIcon aria-label={`delete-${file.name}`} />}
                                                        label={<span className="onboarding-ellipsis" title={file.name}>{file.name}</span>}
                                                        className="onboarding-chip"
                                                    />
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Paper>
            <div className='flexPayment mt-2'>
                {!isViewUserRequest ? <Typography component="em">* Please complete the form above to finalize your onboarding process</Typography> : <Typography component="em"></Typography>}
                <div className="mt-3 alignItemsCenter">
                <Button disabled={activeStep === 0} onClick={handleBack} disableElevation>
                    Back
                </Button>
                <Button
                    disableElevation
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    className='ml-2 cus-seconday-bg-btn btn'
                >
                    Save & Next
                </Button>
                </div>
            </div>
        </Container>
    );
}

KycDetailsUpload.propTypes = {
    isViewUserRequest: PropTypes.bool,
    isApprovedSeller: PropTypes.bool,
    data: PropTypes.object,
    activeStep: PropTypes.number,
    handleBack: PropTypes.func,
    handleNext: PropTypes.func,
}